/**
 * 出庫單詳情 · **下一步（按角色）**（P1-7 ＋ `decisions/018`）
 *
 * 為何抽離（硬規則 B ≤200 行）：以前「發送／取消／交收／場後結算」全部塞喺「執貨摘要」區塊
 * （`OutboundDetailSections.tsx`）而且**冇角色閘** → 倉（`logistics`）都見到 85 職員嘅工作。
 *
 * 角色分工（產品負責人 2026-09-28 定；`decisions/018`）
 *   · 倉（`logistics`）：**只**執貨 ＋ 交收**一掣**；之後收工（帳目／通知／庫存由系統出）
 *   · CoEldery 85 職員（`staff`／`admin`）：發 WhatsApp／取消／**場後結算**／**完單**（交收做後備）
 *   · ⚠️ 場後結算入口只畀職員／管理員（後端 `RECON_ROLES` 一樣擋 —— 倉會 403）
 *
 * 紅線
 *   · 「下一步」一律用後端回嘅 `next`（＝ `nextStatuses()`）—— 前端**唔准**自己列狀態
 *   · 「執貨摘要」區塊嘅**數字**係唯讀推算（後端 `pickSummary()`）—— 冇人可以直接改
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import OutboundDeliverForm from './OutboundDeliverForm';
import OutboundSendPanel from './OutboundSendPanel';
import { btnDanger, btnPrimary, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type Me, type OutboundDetail as Detail } from '../lib/api';

export interface NextStepsSectionProps {
  detail: Detail;
  user: Me;
  onChanged: () => Promise<void>;
}

export function NextStepsSection({ detail, user, onChanged }: NextStepsSectionProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const order = detail.order;

  /** 發通知／取消／場後結算／完單一律職員／管理員（倉只執貨同交收一掣；`decisions/018`） */
  const canManage = user.role === 'staff' || user.role === 'admin';
  if (!order) return null;

  const cancel = async () => {
    if (!window.confirm(t('out.cancelConfirm', { orderNo: order.orderNo }))) return;
    setBusy(true);
    setError(null);
    try {
      await api.updateOutbound(order.id, { action: 'cancel' });
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {order.status === 'draft' && canManage && <OutboundSendPanel orderId={order.id} onDone={onChanged} />}
      {order.status === 'draft' && !canManage && <p style={hintStyle}>{t('out.needStaffToSend')}</p>}

      {order.status === 'sent' && (
        <Link
          to={`/outbound/${order.id}/picklist`}
          style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', textDecoration: 'none', marginTop: '10px' }}
        >
          {t('out.goPick')}
        </Link>
      )}

      {/* 交收一掣（`decisions/018`）：倉做，職員做後備 */}
      {order.status === 'acknowledged' && (
        <OutboundDeliverForm orderId={order.id} kind={order.outboundKind} mode={order.deliveryMode} onDone={onChanged} />
      )}

      {order.status === 'delivered' && order.outboundKind === 'direct_sale' && (
        <p style={hintStyle}>{canManage ? t('out.needsReconciliation') : t('out.needStaffToReconcile')}</p>
      )}

      {/* ⭐ 場後結算／完單：**只** CoEldery 85 職員／管理員（後端 `RECON_ROLES`；`decisions/018`） */}
      {(order.status === 'delivered' || order.status === 'reconciled') &&
        order.outboundKind === 'direct_sale' &&
        canManage && (
          <Link
            to={`/outbound/${order.id}/reconcile`}
            style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', textDecoration: 'none', marginTop: '10px' }}
          >
            {order.status === 'delivered' ? t('recon.goReconcile') : t('recon.goClose')}
          </Link>
        )}

      {order.status === 'cancelled' && <p style={hintStyle}>{t('out.cancelledHint')}</p>}

      {(order.status === 'draft' || order.status === 'sent') && canManage && (
        <div style={{ marginTop: '10px' }}>
          <button type="button" style={btnDanger} onClick={() => void cancel()} disabled={busy}>
            {busy ? t('common.saving') : t('out.cancel')}
          </button>
        </div>
      )}

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
    </>
  );
}
